import clsx from 'clsx'
import { Portal } from 'components/utility/portal'
import { AppToastContainer, showErrorToast } from 'components/utility/toast'
import React, { useEffect, useState } from 'react'
import { selectRequirementTitlesMap, usePackageStore } from 'stores/packageStore'
import { useShallow } from 'zustand/react/shallow'
import { useFlagActions } from '../hooks/useFlagActions'
import RequirementFlagGroup from './RequirementFlagGroup'
import flagPlusIcon from '/app/assets/images/icons/flag-plus.svg'

const CLOSED_STATE = { open: false, requirements: [] }

export default function LockAllConfirmationModal({ packageId }) {
  const [state, setState] = useState(CLOSED_STATE)
  const [confirming, setConfirming] = useState(false)
  const { accept, remove } = useFlagActions(packageId)
  const requirementTitlesMap = usePackageStore(useShallow(selectRequirementTitlesMap))

  useEffect(() => {
    const handler = (e) => {
      setState({ open: true, requirements: e.detail.requirements.map(({ requirementId, flags }) => ({ requirementId, flags })) })
    }

    document.addEventListener('package:lock-all-confirm', handler)
    return () => document.removeEventListener('package:lock-all-confirm', handler)
  }, [])

  useEffect(() => {
    document.body.classList.toggle('overlay-activated', state.open)
    return () => {
      document.body.classList.remove('overlay-activated')
    }
  }, [state.open])

  const handleCancel = () => setState(CLOSED_STATE)

  const handleConfirm = async () => {
    const { fixed, drafted } = state.requirements.flatMap((r) => r.flags).reduce(
      (acc, f) => {
        if (f.state === 'fixed') acc.fixed.push(f)
        else if (f.state === 'drafted') acc.drafted.push(f)
        return acc
      },
      { fixed: [], drafted: [] }
    )

    setConfirming(true)
    try {
      await Promise.all([...fixed.map((f) => accept(f.id)), ...drafted.map((f) => remove(f.id))])
      setState(CLOSED_STATE)
      document.dispatchEvent(new CustomEvent('package:lock-all-confirm-proceed'))
    } catch (error) {
      showErrorToast({ message: 'Something went wrong' })
    } finally {
      setConfirming(false)
    }

  }

  return (
    <Portal>
      <div className={clsx('lock-confirmation-overlay dome-full-width dome-full-height', state.open && 'show')}>
        <div className="dome-full-width dome-full-height dome-d-flex dome-align-center dome-justify-center">
          <div className="lock-confirmation-modal dome-d-flex dome-flex-column dome-gap24">
            <div className="dome-d-flex dome-flex-column dome-gap12">
              <div>
                <img src={flagPlusIcon} />
              </div>
              <div className="dome-h2 dome-color-dark-green">resolve requirement flags?</div>
            </div>
            <div className="dome-p1 dome-color-dark-grey">
              Completing all sections will also clear the following flags across these requirements.
            </div>
            <div className="flag-summary-accordion lock-all-requirement-list">
              {state.requirements.map((req) => (
                <RequirementFlagGroup
                  key={req.requirementId}
                  requirement={{ ...req, requirementName: requirementTitlesMap[req.requirementId] }}
                  hasAccordion={true}
                />
              ))}
            </div>
            <div className="modal-footnote">
              <div className="dome-color-dark-grey dome-text-style-italic">
                You can view flag history by clicking into the requirement to file and using the &ldquo;finished&rdquo;
                tab.
              </div>
            </div>
            <div className="modal-actions">
              <button className="dome-btn dome-btn-link dome-btn-link-back" onClick={handleCancel}>
                back
              </button>
              <button className="dome-btn dome-btn-base dome-btn-red-stroke" onClick={handleConfirm} disabled={confirming}>
                yes, complete all
              </button>
            </div>
          </div>
        </div>
      </div>
      <AppToastContainer />
    </Portal>
  )
}
